import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDropzone } from "react-dropzone";
import { useHistory } from "react-router-dom";
import {
  Box,
  Button,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";

import UploadIcon from "assets/images/icons/upload.svg";
import Alert from "components/shared/Alert";
import { Hero2Text } from "components/shared/Typography";
import { DESIGN_UPLOAD, DESIGN_UPLOAD_VALIDATE } from "helpers/apis";
import useAxios from "helpers/axios";
import { colors } from "theme";

enum DesignStatus {
  UPDATED_DESIGN = "updated_design",
  NEW_DESIGN = "new_design",
  NEW_VERSION = "new_version",
}

interface IValidationResponseDesign {
  bedFilePresent: boolean;
  bedValidation: {
    lineWithBadChromosome: number | null;
    lineWithBadEnd: number | null;
    lineWithBadStart: number | null;
    lineWithBadStrand: number | null;
    lineWithStartGtEnd: number | null;
    lineWithTooFewCols: number | null;
  };
  csvFilePresent: boolean;
  csvValidation: {
    lineWithBadCsvStatus: number | null;
    lineWithTooFewCols: number | null;
    missingColumns: string[];
  };
  ctfFilePresent: boolean;
  ctfValidation: {
    hasContent: boolean;
    missingColumns: string[];
    missingPartNumber: boolean;
    missingMoleculeType: boolean;
    unknownMoleculeType: boolean;
    lineWithBadChromosome: number | null;
    lineWithBadGene: number | null;
    lineWithBadGsp1Boost: number | null;
    lineWithBadGsp2Boost: number | null;
    lineWithBadGsp2Start: number | null;
    lineWithBadGsp2StartGtStop: number | null;
    lineWithBadGsp2Stop: number | null;
    lineWithTooFewCols: number | null;
    multipleGsp1BoostLevels: Array<string | number>;
    multipleGsp2BoostLevels: Array<string | number>;
  };
  designId: string;
  designName: string;
  gtfFilePresent: boolean;
  gtfCnvValidation: {
    lineWithTooFewCols: number | null;
  };
  gtfNocnvValidation: {
    lineWithTooFewCols: number | null;
  };
  passingDesignIdFormat: boolean;
  passingValidation: boolean;
  uploadStatus: DesignStatus;
  version: number;
  yamlFilePresent: boolean;
  yamlValidation: {
    hasCorrectDesignId: boolean;
    hasCorrectDesignType: boolean;
    hasCorrectPlatformType: boolean;
    hasCorrectVersion: boolean;
    hasCorrectWorkflowType: boolean;
    hasDecimalCatalogPrice: boolean;
    hasIntRequiredReads: boolean;
    missingAttributes: string[];
  };
}

interface IValidationResponse {
  containsAtLeastOneSubdir: boolean;
  designs: IValidationResponseDesign[];
  hasUpdates: boolean;
  hasZipExtension: boolean;
  topLevelIsDir: boolean;
  unzipSuccessful: boolean;
}

const useStyles = makeStyles({
  alertRoot: {
    justifyContent: "center",
    margin: "0 -24px",
  },
  alertMessage: {
    fontWeight: 700,
    fontSize: "9.8px",
    lineHeight: "14px",
    letterSpacing: "0.5px",
    textTransform: "uppercase",
  },
  updatedDesignsContent: {
    "& > p:not(:first-of-type)": {
      marginTop: 16,
    },
    "& ul": {
      margin: 0,
      paddingLeft: 26,
      listStyleType: "disc",
      "& > li": {
        fontSize: 16,
        lineHeight: "23.33px",
        letterSpacing: 0.5,
        fontWeight: 400,
        fontFamily: "'Futura Allele', sans-serif",
      },
    },
  },
});

enum UploadDesignStatus {
  EMPTY = "empty",
  INITIAL_VALIDATION_IN_PROGRESS = "initial_validation_in_progress",
  INITIAL_VALIDATION_FAILED = "initial_validation_failed",
  INITIAL_VALIDATION_SUCCEED = "initial_validation_succeed",
  FULL_VALIDATION_FAILED = "full_validation_failed",
  FULL_VALIDATION_SUCCEED = "full_validation_succeed",
  DESIGN_APPROVE = "design_approve",
  UPLOAD_FAILED = "upload_failed",
}

const TwoLines: React.FC<{
  firstLine: string;
  secondLine: string;
  firstLineColor?: "secondary" | "error";
}> = ({ firstLine, secondLine, firstLineColor = "error" }) => (
  <>
    <Typography
      variant="subtitle2"
      align="center"
      color={firstLineColor}
      component="span"
    >
      {firstLine}
    </Typography>
    <Box mt={2}>
      <Typography variant="h5" align="center" component="span">
        {secondLine}
      </Typography>
    </Box>
  </>
);

const createErrorLogUrl = (fileName: string, errors: any[]) => {
  let errorMsgs: string[] = [];
  let subdirectory;

  errors.forEach((error) => {
    subdirectory = error.designId || "";

    if (!error.passingDesignIdFormat) {
      errorMsgs.push(`Subdirectory "${subdirectory}" is not a valid name.`);
    }
    if (!error.yamlFilePresent) {
      errorMsgs.push(`Subdirectory "${subdirectory}" is missing yaml file.`);
    }
    if (!error.bedFilePresent) {
      errorMsgs.push(`Subdirectory "${subdirectory}" is missing bed file.`);
    }
    if (!error.ctfFilePresent) {
      errorMsgs.push(`Subdirectory "${subdirectory}" is missing ctf file.`);
    }
    if (!error.csvFilePresent) {
      errorMsgs.push(`Subdirectory "${subdirectory}" is missing design file.`);
    }
    if (!error.gtfFilePresent) {
      errorMsgs.push(`Subdirectory "${subdirectory}" is missing gtf file.`);
    }
    if (error.gtfValidation?.lineWithTooFewCols > 0) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", gtf file has too few columns in line ${error.gtfValidation?.lineWithTooFewCols}`
      );
    }
    if ((error.ctfValidation?.missingColumns || []).length) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", ctf file is missing these columns:\n ${error.ctfValidation.missingColumns.join(
          "\n"
        )}`
      );
    }
    if (error.ctfValidation?.lineWithBadGene) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", ctf file has bad gene name (as part of primer ID) in line ${error.ctfValidation?.lineWithBadGene}.`
      );
    }
    if (error.ctfValidation?.lineWithBadChromosome) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", ctf file has bad chromosome name (as part of primer ID) in line ${error.ctfValidation?.lineWithBadChromosome}.`
      );
    }
    if (error.ctfValidation?.lineWithBadGsp2Start) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", ctf file has bad gsp2 start in line ${error.ctfValidation?.lineWithBadGsp2Start}.`
      );
    }
    if (error.ctfValidation?.lineWithBadGsp2Stop) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", ctf file has bad gsp2 stop in line ${error.ctfValidation?.lineWithBadGsp2Stop}.`
      );
    }
    if ((error.ctfValidation?.multipleGsp1BoostLevels || []).length) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", ctf file has multiple gsp1 boost levels for "${error.ctfValidation.multipleGsp1BoostLevels.join(
          '","'
        )}"`
      );
    }
    if ((error.ctfValidation?.multipleGsp2BoostLevels || []).length) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", ctf file has multiple gsp2 boost levels for "${error.ctfValidation.multipleGsp2BoostLevels.join(
          '","'
        )}"`
      );
    }
    if (error.ctfValidation?.lineWithBadGsp1Boost) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", ctf file has bad gsp1 boost level in line ${error.ctfValidation?.lineWithBadGsp1Boost}.`
      );
    }
    if (error.ctfValidation?.missingPartNumber) {
      errorMsgs.push(
        `The header line "# PartNumber:" must be present in the CTF file.`
      );
    }
    if (error.ctfValidation?.missingMoleculeType) {
      errorMsgs.push(
        `The header line "# MoleculeType:# must be present in the CTF file.`
      );
    }
    if (error.ctfValidation?.unknownMoleculeType) {
      errorMsgs.push(
        `The header line "# MoleculeType:" should be followed by one of the known molecule types: DNA, RNA, ctDNA and CTDNA.`
      );
    }
    if (error.ctfValidation?.lineWithBadGsp2Boost) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", ctf file has bad gsp2 boost level in line ${error.ctfValidation?.lineWithBadGsp2Boost}.`
      );
    }
    if (error.bedValidation?.lineWithTooFewCols) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", bed file has too few columns in line ${error.bedValidation.lineWithTooFewCols}`
      );
    }
    if (error.bedValidation?.lineWithBadChromosome) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", bed file has bad chromosome name in line ${error.bedValidation.lineWithBadChromosome}`
      );
    }
    if (error.bedValidation?.lineWithBadEnd) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", bed file has stop in line ${error.bedValidation.lineWithBadEnd}`
      );
    }
    if (error.bedValidation?.lineWithBadStart) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", bed file has bad start in line ${error.bedValidation.lineWithBadStart}`
      );
    }
    if (error.bedValidation?.lineWithStartGtEnd) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", bed file has start greater than stop in line ${error.bedValidation.lineWithStartGtEnd}`
      );
    }
    if (error.bedValidation?.lineWithBadStrand) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", bed file has bad strand in line ${error.bedValidation.lineWithBadStrand}`
      );
    }
    if ((error.csvValidation?.missingColumns || []).length) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", details file is missing required columns:\n ${error.csvValidation.missingColumns.join(
          "\n"
        )}`
      );
    }
    if (error.csvValidation?.lineWithBadCnvStatus) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", details file has bad CNV status in line ${error.csvValidation.lineWithBadCnvStatus}.`
      );
    }
    if (!error.yamlValidation?.hasCorrectDesignType) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", yaml file has bad design type.`
      );
    }
    if (!error.yamlValidation?.hasCorrectVersion) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", yaml file has different version numbers.`
      );
    }
    if (!error.yamlValidation?.hasDecimalCatalogPrice) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", there are unexpected characters in decimal field.`
      );
    }
    if (!error.yamlValidation?.hasIntRequiredReads) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", there are unexpected characters in integer field.`
      );
    }
    if (!!error.yamlValidation?.missingAttributes?.length) {
      errorMsgs.push(
        `In subdirectory "${subdirectory}", yaml file are missing following attributes: ${error.yamlValidation.missingAttributes.join(
          ", "
        )}`
      );
    }
  });

  const blob = new Blob(
    [
      `filename: ${fileName}\n`,
      `subdirectory: ${errors
        .map((error) => error.designId || "")
        .join(",")}\n`,
      `error: ${errorMsgs.length ? errorMsgs.join("\n") : "Invalid error."}`,
    ],
    {
      type: "text/plain",
      endings: "native",
    }
  );

  return URL.createObjectURL(blob);
};

const Upload = () => {
  const history = useHistory();
  const [uploadStatus, setUploadStatus] = useState<UploadDesignStatus>(
    UploadDesignStatus.EMPTY
  );
  const [fileUploaded, setFileUploaded] = useState<File | null>(null);
  const [updatedDesigns, setUpdatedDesigns] = useState<
    IValidationResponseDesign[]
  >([]);
  const [error, setError] = useState<any>(null);
  const errorDownloadRef = useRef<string | null>(null);
  const xs = useMediaQuery((theme: Theme) => theme.breakpoints.only("xs"));

  const classes = useStyles();

  const { fetchData, loading: isUploading } = useAxios({
    config: { url: DESIGN_UPLOAD_VALIDATE, method: "POST" },
    callOnInit: false,
  });
  const { getRootProps, getInputProps } = useDropzone({
    accept: ".zip",
    multiple: false,
    validator: (file: File) => {
      const formData = new FormData();
      formData.append("file", file);

      setUploadStatus(UploadDesignStatus.INITIAL_VALIDATION_IN_PROGRESS);

      fetchData(
        { data: formData, params: { initialValidationOnly: true } },
        () => {
          setUploadStatus(UploadDesignStatus.INITIAL_VALIDATION_SUCCEED);
          setFileUploaded(file);
        },
        (error: any) => {
          setUploadStatus(UploadDesignStatus.INITIAL_VALIDATION_FAILED);
          setError(error?.data || {});
        }
      );

      return null;
    },
  });

  const versionUpdatedDesigns = useMemo(
    () =>
      updatedDesigns.filter(
        (updatedDesign: IValidationResponseDesign) =>
          updatedDesign.uploadStatus === DesignStatus.UPDATED_DESIGN
      ),
    [updatedDesigns]
  );
  const versionNewDesigns = useMemo(
    () =>
      updatedDesigns.filter(
        (updatedDesign: IValidationResponseDesign) =>
          updatedDesign.uploadStatus === DesignStatus.NEW_VERSION
      ),
    [updatedDesigns]
  );

  useEffect(() => {
    // Full file validation
    if (fileUploaded) {
      const formData = new FormData();
      formData.append("file", fileUploaded);

      fetchData(
        { data: formData },
        ({ response }: any) => {
          if (response?.data) {
            const validationResponse: IValidationResponse = response.data;
            if (validationResponse.hasUpdates) {
              setUploadStatus(UploadDesignStatus.DESIGN_APPROVE);
              setUpdatedDesigns(validationResponse.designs);
            } else {
              setUploadStatus(UploadDesignStatus.FULL_VALIDATION_SUCCEED);
            }
          } else {
            setUploadStatus(UploadDesignStatus.FULL_VALIDATION_FAILED);
          }
        },
        (error: any) => {
          setUploadStatus(UploadDesignStatus.FULL_VALIDATION_FAILED);
          setError(error?.data || {});
        }
      );
    } else {
      setUploadStatus(UploadDesignStatus.EMPTY);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploaded]);

  const formDesc = useMemo(() => {
    switch (uploadStatus) {
      case UploadDesignStatus.INITIAL_VALIDATION_IN_PROGRESS:
        return "Initial file validation in progress.";
      case UploadDesignStatus.INITIAL_VALIDATION_SUCCEED:
        return "Initial file validation complete. Full file validation in progress.";
      case UploadDesignStatus.INITIAL_VALIDATION_FAILED:
        if (
          error?.unzipSuccessful === false ||
          error?.containsAtLeastOneSubdir === false
        ) {
          return (
            <TwoLines
              firstLine={`${
                error?.unzipSuccessful === false
                  ? "Cannot open .zip file."
                  : "Incorrect file structure."
              } Please try again.`}
              secondLine={
                error?.unzipSuccessful === false
                  ? "Uploaded file must be zipped."
                  : "Unzipped file must contain at least one subdirectory."
              }
            />
          );
        }
        return (
          <Typography
            variant="subtitle2"
            align="center"
            color="error"
            component="span"
          >
            Invalid Error
          </Typography>
        );
      case UploadDesignStatus.FULL_VALIDATION_FAILED:
        const failedDesigns = Array.isArray(error?.designs)
          ? error.designs.filter(
              (design: IValidationResponseDesign) => !design.passingValidation
            )
          : [];
        errorDownloadRef.current = createErrorLogUrl(
          fileUploaded!.name,
          failedDesigns.length ? failedDesigns : []
        );
        return (
          <TwoLines
            firstLine="Validation unsuccessful."
            secondLine="Please try again."
          />
        );
      case UploadDesignStatus.FULL_VALIDATION_SUCCEED:
        setError(null);
        return (
          <TwoLines
            firstLine="Validation successful."
            secondLine='Please select "Upload Designs" to complete upload process.'
            firstLineColor="secondary"
          />
        );
      case UploadDesignStatus.DESIGN_APPROVE:
        return (
          <Typography
            variant="subtitle2"
            style={{ color: colors.semantic.warning }}
            component="span"
          >
            Validation Successful: Existing design IDs and/or versions present.
          </Typography>
        );
      default:
        return "Upload .zip file";
    }
  }, [uploadStatus, error, fileUploaded]);

  const handleDesignUpload = useCallback(() => {
    const formData = new FormData();
    formData.append("file", fileUploaded!);

    fetchData(
      { data: formData, url: DESIGN_UPLOAD, params: { updateOk: true } },
      () => {
        history.push("/dashboard/designs", { from: "upload-designs" });
      },
      () => {
        setUploadStatus(UploadDesignStatus.UPLOAD_FAILED);
      }
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileUploaded, history]);

  const resetUploadForm = useCallback(() => {
    setUploadStatus(UploadDesignStatus.EMPTY);
    setUpdatedDesigns([]);
    setFileUploaded(null);
  }, []);

  return (
    <Box className="design-upload-panel">
      {uploadStatus !== UploadDesignStatus.UPLOAD_FAILED && (
        <Hero2Text align={xs ? "center" : "left"}>Validate File</Hero2Text>
      )}
      {uploadStatus === UploadDesignStatus.UPLOAD_FAILED && (
        <Alert
          icon={false}
          severity="warning"
          noDismiss
          classes={{ root: classes.alertRoot, message: classes.alertMessage }}
        >
          We're experiencing some problems. Try again.
        </Alert>
      )}
      <Box mt={2} mb={3}>
        <Typography variant="subtitle1">
          Please review the Requirements prior to beginning the validation
          process.
        </Typography>
      </Box>
      <Box display="flex" flexDirection="column" alignItems="center">
        <Box
          width={1}
          boxSizing="border-box"
          px={3}
          py={4}
          mb={4}
          className="default-bg"
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <img src={UploadIcon} alt="Upload Icon" />
          <Box my={2}>
            <Typography variant="subtitle1" align="center">
              {formDesc}
            </Typography>
          </Box>
          {[
            UploadDesignStatus.EMPTY,
            UploadDesignStatus.INITIAL_VALIDATION_FAILED,
            UploadDesignStatus.FULL_VALIDATION_FAILED,
            UploadDesignStatus.UPLOAD_FAILED,
          ].includes(uploadStatus) && (
            <Box {...getRootProps({ className: "dropzone" })}>
              <input {...getInputProps()} />
              <Button
                variant="outlined"
                color="primary"
                size="small"
                onClick={() =>
                  uploadStatus === UploadDesignStatus.UPLOAD_FAILED &&
                  setFileUploaded(null)
                }
              >
                Browse file
              </Button>
            </Box>
          )}
          {uploadStatus === UploadDesignStatus.DESIGN_APPROVE &&
            updatedDesigns.length > 0 && (
              <Box className={classes.updatedDesignsContent}>
                {versionUpdatedDesigns.length > 0 && (
                  <>
                    <Typography>
                      Existing versions of the following designs will be
                      overwritten:
                    </Typography>
                    <ul>
                      {versionUpdatedDesigns.map(
                        (design: IValidationResponseDesign) => (
                          <li key={design.designId}>
                            <Typography>
                              {`[${design.designId}, version ${design.version}] - ${design.designName}`}
                            </Typography>
                          </li>
                        )
                      )}
                    </ul>
                  </>
                )}

                {versionNewDesigns.length > 0 && (
                  <>
                    <Typography>
                      New versions of the following designs will be created:
                    </Typography>
                    <ul>
                      {versionNewDesigns.map(
                        (design: IValidationResponseDesign) => (
                          <li key={design.designId}>
                            <Typography>
                              {`[${design.designId}, version ${design.version}] - ${design.designName}`}
                            </Typography>
                          </li>
                        )
                      )}
                    </ul>
                  </>
                )}

                <Typography>
                  If this is correct, Approve & upload. To change the version
                  for a design, please Cancel to upload a new file.
                </Typography>
              </Box>
            )}
        </Box>

        {![
          UploadDesignStatus.FULL_VALIDATION_FAILED,
          UploadDesignStatus.DESIGN_APPROVE,
        ].includes(uploadStatus) && (
          <Button
            variant="contained"
            color="primary"
            disabled={
              !fileUploaded ||
              !!error ||
              isUploading ||
              uploadStatus === UploadDesignStatus.UPLOAD_FAILED
            }
            onClick={handleDesignUpload}
            fullWidth={xs}
          >
            Upload designs
          </Button>
        )}
        {uploadStatus === UploadDesignStatus.FULL_VALIDATION_FAILED && (
          <Button
            component="a"
            variant="contained"
            color="primary"
            href={errorDownloadRef.current!}
            download="error_log.txt"
          >
            Download error log
          </Button>
        )}
        {uploadStatus === UploadDesignStatus.DESIGN_APPROVE && (
          <Box display="flex" justifyContent="center" style={{ gap: 16 }}>
            <Button
              variant="outlined"
              color="primary"
              onClick={resetUploadForm}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleDesignUpload}
              disabled={isUploading}
            >
              Approve & upload
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default React.memo(Upload);
